import React, { useState, useEffect, useCallback } from 'react'
import useApi from '../../../hooks/useApi'
import { getUserCourseStatus } from '../../../api/supabaseApi'
import DataTable from 'react-data-table-component'
import Search from '../../../components/icons/Search'
import { useNavigate } from 'react-router-dom'
import debounce from 'lodash/debounce'
import { renderStatus } from '../../../utils/helpers'
import TableSkeleton from '../../../components/common/TableSkeleton'
import PrimaryButton from "../../../components/buttons/PrimaryButton";
import Loading from "../../../components/common/Loading";
import {supabase} from "../../../services/supabaseClient";

const UserTable = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [currentTab, setCurrentTab] = useState('all')
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [loadingExport, setLoadingExport] = useState(false)
  const { data, loading, error } = useApi(
    () => getUserCourseStatus(page, rowsPerPage, searchTerm, currentTab),
    [page, rowsPerPage, currentTab, searchTerm, currentTab]
  )

  const debouncedSearchTerm = useCallback(
    debounce((value) => {
      setSearchTerm(value)
    }, 600),
    []
  )

  const tabs = [
    { key: 'all', label: 'All' },
    { key: 'in_progress', label: 'In Progress' },
    { key: 'completed', label: 'Completed' },
    { key: 'not_started', label: 'Not Started' },
  ]

  useEffect(() => {
    if (!loading && data) {
      setUsers(data.users)
      setTotalRows(data.total)
    }
  }, [data])

  const renderAvatar = (user) => {
    const url = user.avatar_url
      ? user.avatar_url.replace('.glb', '.png')
      : '/images/avatar_default.png'
    return (
      <div className="flex justify-start items-center gap-2">
        <img
          src={url}
          alt="Profile"
          className="w-8 h-8 rounded-full object-top object-cover bg-[#B0CBBE]"
        />
        <p>{user.full_name || 'N/A'}</p>
      </div>
    )
  }

  const columns = [
    {
      name: 'Name',
      selector: (row) => renderAvatar(row),
      sortable: false,
    },
    {
      name: 'User Id',
      selector: (row) => row.user_id,
      sortable: false,
    },
    {
      name: 'Email',
      selector: (row) => row.real_email || 'N/A',
      sortable: false,
    },
    {
      name: 'Status',
      selector: (row) => renderStatus(row.overall_status),
      sortable: false,
    },
    {
      name: 'Badge Assign',
      selector: (row) => `${row.completed_badges}/${row.total_badges}`,
      sortable: false,
    },
    {
      name: 'Ikigai Badge',
      selector: (row) => `${row.has_specific_badge ? 1 : 0}/1`,
      sortable: false,
    },
  ]

  const handlePageChange = (page) => {
    setPage(page)
  }

  const handlePerRowsChange = async (newPerPage) => {
    setRowsPerPage(newPerPage)
  }

  const handleSearch = (event) => {
    setSearchInput(event.target.value)
    debouncedSearchTerm(event.target.value)
  }

  const handleRowClick = (row) => {
    navigate(`/admin/users/${row.user_id}`)
  }

  const handleExport = async () => {
    const {data, error} = await supabase
      .from('user_course_status_2')
      .select('user_id, full_name, real_email, completed_badges, has_specific_badge')
      .order('created_at', { ascending: false });

    if (error) {
      console.log(error)
      return;
    }

    // Convert data to CSV format
    const csvData = [];

    // Add headers
    csvData.push(['User ID', 'Full Name', 'Email', 'Completed Badges', 'Ikigai Badge']);

    // Add rows
    data.forEach((row) => {
      csvData.push([row.user_id, row.full_name, row.real_email, row.completed_badges, `${row.has_specific_badge ? 1 : 0}/1`]);
    });

    // Convert to string
    const csvString = csvData.map((line) => line.join(',')).join('\n');

    // Create a Blob and download
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_course_data.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Revoke the URL to release memory
    URL.revokeObjectURL(url);
  }

  return (
    <div className="container mx-auto">
      <div className="p-4 bg-primary rounded-lg">
        <p className="text-2xl text-white font-bold my-4">Users</p>
      </div>

      <div className="p-4 my-4 bg-white rounded-lg shadow-xl">
        <div className="my-4 relative">
          <ul className="flex flex-nowrap overflow-scroll">
            {tabs.map((tab) => (
              <li key={tab.key} className="me-2">
                <a
                  onClick={() => setCurrentTab(tab.key)}
                  className={`cursor-pointer text-nowrap text-base inline-block py-2 px-4 rounded-t-lg ${
                    currentTab === tab.key
                      ? 'text-black border-b-2 border-secondary-600'
                      : 'text-grey-700'
                  }`}
                >
                  {tab.label}
                </a>
              </li>
            ))}
          </ul>
          <PrimaryButton
            disabled={loadingExport}
            onClick={handleExport}
            className='absolute right-2 top-1/2 transform -translate-y-1/2'>
            Export {loadingExport && <Loading />}
          </PrimaryButton>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Search"
            value={searchInput}
            onChange={handleSearch}
            className="border border-gray-200 rounded-lg p-2 pl-8 mb-4 w-full"
          />
          <span className="absolute left-2 top-2">
            <Search color="black" />
          </span>
        </div>
        {loading ? (
          <TableSkeleton rows={rowsPerPage} cols={5} />
        ) : (
          <DataTable
            columns={columns}
            data={users}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationDefaultPage={page}
            paginationPerPage={rowsPerPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            onRowClicked={handleRowClick}
          />
        )}
      </div>
    </div>
  )
}

export default UserTable
